import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/cn";

export const surfaceVariants = cva("relative text-ink", {
  variants: {
    tone: {
      /** Default card: one step above the canvas, stacked hairline rings. */
      raised: "bg-surface-1 shadow-material",
      /** Inset stage inside a card (libraries.dev "card-stage"). */
      well: "bg-well shadow-[inset_0_0_0_1px_var(--vd-hairline)]",
      /** Flat panel with a hairline, for dense console layouts. */
      flat: "bg-surface-1 shadow-[inset_0_0_0_1px_var(--vd-hairline)]",
      /** Floating content: menus, popovers. Near-opaque by rule. */
      overlay: "bg-overlay shadow-popover",
    },
    radius: {
      md: "rounded-md",
      lg: "rounded-lg",
      xl: "rounded-xl",
      "2xl": "rounded-2xl",
    },
  },
  defaultVariants: { tone: "raised", radius: "lg" },
});

export interface SurfaceProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof surfaceVariants> {
  asChild?: boolean;
}

/** Base container. Pick a tone; never hand-roll a card background. */
export const Surface = forwardRef<HTMLDivElement, SurfaceProps>(function Surface(
  { tone, radius, asChild, className, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : "div";
  return <Comp ref={ref} className={cn(surfaceVariants({ tone, radius }), className)} {...props} />;
});

export interface CardProps extends SurfaceProps {
  /** Padding preset. Compact is for dense lists in the desktop app. */
  padding?: "none" | "compact" | "default";
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { padding = "default", className, ...props },
  ref,
) {
  return (
    <Surface
      ref={ref}
      className={cn(
        "flex flex-col",
        padding === "default" && "gap-3 p-4",
        padding === "compact" && "gap-2 p-3",
        className,
      )}
      {...props}
    />
  );
});

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title: ReactNode;
  /** Small leading glyph, 16px. */
  icon?: ReactNode;
  /** Right-aligned actions or a status pill. */
  actions?: ReactNode;
  description?: ReactNode;
}

export function CardHeader({
  title,
  icon,
  actions,
  description,
  className,
  ...props
}: CardHeaderProps) {
  return (
    <div className={cn("flex min-h-7 items-start justify-between gap-3", className)} {...props}>
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex items-center gap-2 font-medium text-title text-ink [&_svg]:size-4 [&_svg]:text-ink-3">
          {icon}
          <span className="truncate">{title}</span>
        </div>
        {description ? <p className="text-caption text-ink-3">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
    </div>
  );
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex items-center justify-end gap-2 border-hairline border-t pt-3", className)}
      {...props}
    />
  );
}

/** A 1px hairline rule. */
export function Divider({
  orientation = "horizontal",
  className,
}: {
  orientation?: "horizontal" | "vertical";
  className?: string;
}) {
  return (
    <hr
      aria-orientation={orientation}
      className={cn(
        "shrink-0 border-0 bg-hairline",
        orientation === "horizontal" ? "h-px w-full" : "h-auto w-px self-stretch",
        className,
      )}
    />
  );
}
